/**
 * /admin/users — Student & Parent Directory.
 * View all accounts, edit fields, reset password, activate/deactivate, delete
 * (individual + bulk + "created before a date"), and a full audit log of changes.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, Search, Pencil, KeyRound, Trash2, ShieldCheck, ArrowLeft,
  UserX, UserCheck, History, X, AlertTriangle, MailPlus, Copy, CheckCircle2, Trophy,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const todayStr = () => new Date().toISOString().slice(0, 10);

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState({});
  const [editUser, setEditUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [magicUser, setMagicUser] = useState(null);
  const [siqUser, setSiqUser] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [beforeDate, setBeforeDate] = useState(todayStr());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/academy/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`, { headers: authHeaders() });
      setUsers(r.data.users || []);
      setDenied(false);
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) setDenied(true);
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const toggleActive = async (u) => {
    try {
      await api.post(`/academy/admin/users/${u.id}/active`, { active: !(u.active !== false) }, { headers: authHeaders() });
      toast.success(u.active !== false ? 'Account deactivated' : 'Account activated');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/academy/admin/users/${u.id}`, { headers: authHeaders() });
      toast.success(`Deleted ${u.email}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const bulkDeleteSelected = async () => {
    if (selectedIds.length === 0) { toast.error('Select accounts first'); return; }
    if (!window.confirm(`Delete ${selectedIds.length} selected account(s)? Protected admin accounts are always kept.`)) return;
    try {
      const r = await api.post('/academy/admin/users/bulk-delete', { ids: selectedIds }, { headers: authHeaders() });
      toast.success(`Deleted ${r.data.deleted_count} account(s)`);
      setSelected({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const deleteBeforeDate = async () => {
    if (!beforeDate) { toast.error('Pick a date'); return; }
    if (!window.confirm(`Delete ALL accounts created before ${beforeDate}? Your protected admin/teacher logins are always kept. This cannot be undone.`)) return;
    try {
      const r = await api.post('/academy/admin/users/bulk-delete', { before_date: beforeDate }, { headers: authHeaders() });
      toast.success(`Deleted ${r.data.deleted_count} account(s). Kept ${r.data.protected_skipped?.length || 0} protected.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (denied) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <ShieldCheck className="w-10 h-10 text-orange-500 mx-auto mb-3" />
          <h1 className="text-xl font-black">Administrator access required</h1>
          <p className="text-sm text-gray-500 mt-2">Sign in from the Admin Hub with an administrator account.</p>
          <Link to="/admin" className="inline-block mt-4 text-xs font-black uppercase tracking-widest text-orange-400">← Back to Admin Hub</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-20" data-testid="admin-users-page">
      <div className="max-w-7xl mx-auto px-4 pt-20">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 hover:text-white"><ArrowLeft className="w-3.5 h-3.5" /> Admin Hub</Link>
            <h1 className="text-2xl font-black uppercase tracking-tight mt-2">Student &amp; Parent Directory</h1>
            <p className="text-xs text-gray-500 mt-1">{users.length} accounts · edit any field, reset passwords, deactivate or delete. Protected admin logins are locked.</p>
          </div>
          <button onClick={() => setShowAudit(true)} className="flex items-center gap-2 border-2 border-gray-800 hover:border-orange-500 px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.2em]" data-testid="open-audit-log-btn">
            <History className="w-4 h-4" /> Change Log
          </button>
        </div>

        {/* Search + bulk actions */}
        <div className="mt-6 flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="flex-1 flex items-center gap-2 bg-zinc-950 border-2 border-gray-800 px-3">
            <Search className="w-4 h-4 text-gray-600" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, parent email…" className="flex-1 bg-transparent py-2.5 text-sm outline-none" data-testid="user-search" />
          </div>
          <button onClick={bulkDeleteSelected} disabled={selectedIds.length === 0} className="flex items-center justify-center gap-2 bg-red-500/90 hover:bg-red-500 disabled:bg-gray-800 disabled:text-gray-600 text-white px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.2em]" data-testid="bulk-delete-btn">
            <Trash2 className="w-4 h-4" /> Delete Selected ({selectedIds.length})
          </button>
        </div>

        {/* Delete-before-date cleanup */}
        <div className="mt-3 flex flex-wrap items-center gap-3 border-2 border-amber-500/40 bg-amber-500/[0.05] p-3" data-testid="delete-before-tool">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <span className="text-xs text-gray-300 font-bold">Clean up old accounts:</span>
          <span className="text-xs text-gray-500">delete everything created before</span>
          <input type="date" value={beforeDate} onChange={(e) => setBeforeDate(e.target.value)} className="bg-black border-2 border-gray-800 px-2 py-1.5 text-sm" data-testid="before-date-input" />
          <button onClick={deleteBeforeDate} className="bg-amber-500 hover:bg-amber-400 text-black px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]" data-testid="delete-before-btn">Delete Before Date</button>
          <span className="text-[11px] text-gray-600">(your admin/teacher logins are always kept)</span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
        ) : (
          <div className="mt-4 overflow-x-auto border border-gray-900">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="text-gray-500 uppercase tracking-widest text-[9px] border-b border-gray-800 bg-zinc-950">
                  <th className="p-2 w-8"></th>
                  <th className="p-2">Name</th><th className="p-2">Email</th><th className="p-2">Parent Email</th>
                  <th className="p-2">Grade</th><th className="p-2">Role</th><th className="p-2">Created</th><th className="p-2">Status</th><th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-gray-900 hover:bg-white/[0.02]" data-testid={`user-row-${u.id}`}>
                    <td className="p-2">
                      {!u.protected && <input type="checkbox" checked={!!selected[u.id]} onChange={(e) => setSelected((s) => ({ ...s, [u.id]: e.target.checked }))} data-testid={`user-select-${u.id}`} />}
                    </td>
                    <td className="p-2 font-bold text-white">{u.name || '—'} {u.protected && <ShieldCheck className="inline w-3 h-3 text-emerald-400 ml-1" title="Protected admin" />}</td>
                    <td className="p-2 text-gray-300">{u.email}</td>
                    <td className="p-2 text-gray-400">{u.parent_email || '—'}</td>
                    <td className="p-2">{u.grade_level || '—'}</td>
                    <td className="p-2"><span className={`uppercase text-[9px] font-black ${u.role === 'teacher' ? 'text-orange-400' : 'text-gray-400'}`}>{u.role}{u.is_admin ? ' · admin' : ''}</span></td>
                    <td className="p-2 text-gray-500 whitespace-nowrap">{(u.created_at || '').slice(0, 10)}</td>
                    <td className="p-2">{u.active === false ? <span className="text-red-400 font-bold">Inactive</span> : <span className="text-emerald-400">Active</span>}</td>
                    <td className="p-2">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setEditUser(u)} className="text-gray-400 hover:text-white" title="Edit" data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setPwUser(u)} className="text-gray-400 hover:text-blue-400" title="Reset password" data-testid={`reset-pw-${u.id}`}><KeyRound className="w-4 h-4" /></button>
                        <button onClick={() => setMagicUser(u)} className="text-gray-400 hover:text-orange-400" title="Send welcome / password-set link" data-testid={`send-magic-${u.id}`}><MailPlus className="w-4 h-4" /></button>
                        <button onClick={() => setSiqUser(u)} className="text-gray-400 hover:text-emerald-400" title="Comp Sport IQ access" data-testid={`siq-comp-${u.id}`}><Trophy className="w-4 h-4" /></button>
                        {!u.protected && (
                          <>
                            <button onClick={() => toggleActive(u)} className="text-gray-400 hover:text-amber-400" title={u.active === false ? 'Activate' : 'Deactivate'} data-testid={`toggle-active-${u.id}`}>
                              {u.active === false ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                            </button>
                            <button onClick={() => del(u)} className="text-gray-400 hover:text-red-400" title="Delete" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editUser && <EditModal user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />}
      {pwUser && <PasswordModal user={pwUser} onClose={() => setPwUser(null)} />}
      {magicUser && <MagicLinkModal user={magicUser} onClose={() => setMagicUser(null)} />}
      {siqUser && <SportIQCompModal user={siqUser} onClose={() => setSiqUser(null)} />}
      {showAudit && <AuditModal onClose={() => setShowAudit(false)} />}
    </div>
  );
};

const FIELDS = [
  ['name', 'Name'], ['email', 'Email'], ['parent_email', 'Parent Email'],
  ['grade_level', 'Grade'], ['role', 'Role'], ['sport', 'Sport'], ['position', 'Position'], ['city', 'City'], ['phone', 'Phone'],
];

const EditModal = ({ user, onClose, onSaved }) => {
  const [form, setForm] = useState(() => Object.fromEntries(FIELDS.map(([k]) => [k, user[k] || ''])));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      FIELDS.forEach(([k]) => { if ((form[k] || '') !== (user[k] || '')) payload[k] = form[k]; });
      if (Object.keys(payload).length === 0) { toast.info('No changes'); onClose(); return; }
      await api.patch(`/academy/admin/users/${user.id}`, payload, { headers: authHeaders() });
      toast.success('Account updated');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Edit — ${user.email}`} onClose={onClose} testid="edit-user-modal">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, label]) => (
          <label key={k} className="text-xs">
            <span className="text-gray-500 uppercase tracking-wider text-[9px] font-bold">{label}</span>
            <input value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full bg-black border-2 border-gray-800 px-3 py-2 text-sm focus:border-orange-500 outline-none" data-testid={`edit-field-${k}`} />
          </label>
        ))}
      </div>
      <button onClick={save} disabled={busy} className="mt-4 w-full bg-orange-500 hover:bg-orange-400 text-black py-3 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2" data-testid="edit-user-save">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save Changes
      </button>
    </Modal>
  );
};

const PasswordModal = ({ user, onClose }) => {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (pw.length < 6) { toast.error('At least 6 characters'); return; }
    setBusy(true);
    try {
      await api.post(`/academy/admin/users/${user.id}/password`, { new_password: pw }, { headers: authHeaders() });
      toast.success(`Password reset for ${user.email}`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Reset Password — ${user.email}`} onClose={onClose} testid="reset-pw-modal">
      <p className="text-xs text-gray-500 mb-2">Set a new password for this account and share it securely with the family.</p>
      <input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className="w-full bg-black border-2 border-gray-800 px-3 py-2.5 text-sm font-mono focus:border-blue-500 outline-none" data-testid="new-password-input" />
      <button onClick={save} disabled={busy} className="mt-4 w-full bg-blue-500 hover:bg-blue-400 text-white py-3 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2" data-testid="reset-pw-save">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Set New Password
      </button>
    </Modal>
  );
};

const MagicLinkModal = ({ user, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const fire = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/academy/admin/users/${user.id}/send-magic-link`, {}, { headers: authHeaders() });
      setResult(r.data);
      if (r.data.email_sent) {
        toast.success(`Welcome email sent to ${user.email}`);
      } else {
        toast.warning(`Link created — Resend delivery failed. Copy the link and share it manually.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send magic link');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!result?.reset_link) return;
    try {
      await navigator.clipboard.writeText(result.reset_link);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  return (
    <Modal title={`Send Welcome Link — ${user.email}`} onClose={onClose} testid="magic-link-modal">
      {!result ? (
        <>
          <p className="text-xs text-gray-400 mb-3">
            Sends <b className="text-white">{user.name || user.email}</b> a branded "Welcome — Set Your Password" email via Resend.
            The link expires in <b className="text-orange-400">24 hours</b>.
          </p>
          <div className="border border-gray-900 bg-black/40 p-3 text-[11px] text-gray-500 mb-4">
            <b className="text-gray-300 block mb-1">Why use this instead of "Reset Password"?</b>
            • Bypasses the anti-enumeration silence of the public forgot-password flow.<br />
            • Also returns the link to you here — so if inbox delivery fails, you can copy-paste and text it directly.
          </div>
          <button
            onClick={fire}
            disabled={busy}
            className="w-full bg-orange-500 hover:bg-orange-400 text-black py-3 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2"
            data-testid="magic-link-send"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailPlus className="w-4 h-4" />}
            Send Welcome Link
          </button>
        </>
      ) : (
        <div className="space-y-3">
          <div className={`border p-3 text-xs flex items-start gap-2 ${result.email_sent ? 'border-emerald-700/60 bg-emerald-900/10' : 'border-amber-700/60 bg-amber-900/10'}`}>
            {result.email_sent ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />}
            <div>
              <div className={`font-black uppercase tracking-wider text-[10px] ${result.email_sent ? 'text-emerald-400' : 'text-amber-400'}`}>
                {result.email_sent ? 'Email sent via Resend' : 'Resend delivery failed'}
              </div>
              <div className="text-gray-400 mt-1">
                {result.email_sent
                  ? `Delivered to ${result.email}. Expires in 24 hours.`
                  : (result.email_error || 'Copy the link below and share it manually.')}
              </div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-orange-400 font-bold mb-1">Password-Set Link (backup)</div>
            <div className="flex items-stretch gap-2">
              <input
                readOnly
                value={result.reset_link}
                onClick={(e) => e.target.select()}
                className="flex-1 bg-black border-2 border-gray-800 px-3 py-2 text-[11px] font-mono text-gray-300 outline-none"
                data-testid="magic-link-url"
              />
              <button
                onClick={copyLink}
                className="px-3 bg-orange-500 hover:bg-orange-400 text-black text-[10px] font-black uppercase tracking-wider flex items-center gap-1"
                data-testid="magic-link-copy"
              >
                {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="text-[10px] text-gray-600 mt-1.5">
              Safe to text or share directly. Single-use, expires {(result.expires_at || '').replace('T', ' ').slice(0, 16)} UTC.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-full border border-gray-800 hover:border-gray-700 text-gray-400 hover:text-white py-2.5 text-[10px] font-black uppercase tracking-[0.2em]"
            data-testid="magic-link-done"
          >
            Done
          </button>
        </div>
      )}
    </Modal>
  );
};

const SPORT_IQ_SPORTS = [
  { key: 'basketball', label: 'Basketball', accent: '#FF5722' },
  { key: 'football', label: 'Football', accent: '#3B82F6' },
  { key: 'soccer', label: 'Soccer', accent: '#22C55E' },
  { key: 'track', label: 'Track', accent: '#EAB308' },
];

const SportIQCompModal = ({ user, onClose }) => {
  const [sport, setSport] = useState('basketball');
  const [mode, setMode] = useState('full');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [access, setAccess] = useState({}); // {sport: {access, mode, unlocked_week, reason, started_at}}

  const loadAll = async () => {
    const map = {};
    await Promise.all(SPORT_IQ_SPORTS.map(async (s) => {
      try {
        // Admin-scoped: check the target user's current state via a lightweight endpoint.
        // We call the access-log listing and pluck this user's entry.
        const r = await api.get('/academy/admin/sport-iq/access-log', { headers: authHeaders() });
        const row = (r.data.users || []).find((u) => u.email?.toLowerCase() === user.email?.toLowerCase());
        map[s.key] = row?.sport_iq_access?.[s.key] || null;
      } catch {
        map[s.key] = null;
      }
    }));
    setAccess(map);
  };

  useEffect(() => { loadAll(); }, [user.id]);

  const comp = async () => {
    setBusy(true);
    try {
      await api.post('/academy/admin/sport-iq/comp',
        { email: user.email, sport, mode, note: note || undefined },
        { headers: authHeaders() });
      toast.success(`${SPORT_IQ_SPORTS.find((s) => s.key === sport).label} · ${mode.toUpperCase()} comped to ${user.email}`);
      setNote('');
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Comp failed'); }
    finally { setBusy(false); }
  };

  const revoke = async (targetSport) => {
    if (!window.confirm(`Revoke ${targetSport} access for ${user.email}?`)) return;
    setBusy(true);
    try {
      await api.post('/academy/admin/sport-iq/revoke',
        { email: user.email, sport: targetSport },
        { headers: authHeaders() });
      toast.success(`${targetSport} access revoked`);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Revoke failed'); }
    finally { setBusy(false); }
  };

  const isAdminUser = user.is_admin || user.role === 'teacher' || user.role === 'founder';

  return (
    <Modal title={`Sport IQ Access — ${user.email}`} onClose={onClose} testid="siq-comp-modal" wide>
      {isAdminUser && (
        <div className="border border-emerald-700/40 bg-emerald-900/10 p-3 mb-4 text-[11px] text-emerald-300 flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div><b className="uppercase tracking-wider">Auto-comped:</b> This user is an admin/teacher and already has full access to every Sport IQ course. Comps below are still recorded for audit but redundant.</div>
        </div>
      )}

      {/* Current state grid */}
      <div className="grid grid-cols-2 gap-2 mb-5" data-testid="siq-current-state">
        {SPORT_IQ_SPORTS.map((s) => {
          const a = access[s.key];
          return (
            <div key={s.key} className="border border-gray-900 p-3" style={{ borderLeftColor: s.accent, borderLeftWidth: 3 }}>
              <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: s.accent }}>{s.label}</div>
              {a ? (
                <>
                  <div className="text-[11px] text-white mt-1"><b className="uppercase">{a.mode}</b> {a.mode === 'drip' && a.started_at && <span className="text-gray-500">· since {a.started_at.slice(0,10)}</span>}</div>
                  <div className="text-[9px] text-gray-500 mt-0.5">by {a.granted_by || 'self'}</div>
                  <button onClick={() => revoke(s.key)} disabled={busy} className="mt-2 text-[9px] font-black uppercase tracking-widest text-red-400 hover:text-red-300 disabled:opacity-50" data-testid={`siq-revoke-${s.key}`}>Revoke</button>
                </>
              ) : (
                <div className="text-[11px] text-gray-600 mt-1">{isAdminUser ? 'Admin comp · no override' : 'No access'}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Grant form */}
      <div className="border border-gray-900 p-4 space-y-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1.5 font-black">Sport</div>
          <div className="grid grid-cols-4 gap-1.5">
            {SPORT_IQ_SPORTS.map((s) => (
              <button key={s.key} onClick={() => setSport(s.key)}
                className={`text-[10px] font-black uppercase tracking-widest px-2 py-2 border-2 ${sport === s.key ? 'text-black' : 'text-gray-400 border-gray-800 hover:border-gray-600'}`}
                style={sport === s.key ? { backgroundColor: s.accent, borderColor: s.accent } : {}}
                data-testid={`siq-select-sport-${s.key}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1.5 font-black">Mode</div>
          <div className="grid grid-cols-2 gap-1.5">
            <button onClick={() => setMode('full')} className={`text-[10px] font-black uppercase tracking-widest px-2 py-2.5 border-2 ${mode === 'full' ? 'bg-orange-500 border-orange-500 text-black' : 'text-gray-400 border-gray-800'}`} data-testid="siq-select-mode-full">Full · Skip Around</button>
            <button onClick={() => setMode('drip')} className={`text-[10px] font-black uppercase tracking-widest px-2 py-2.5 border-2 ${mode === 'drip' ? 'bg-orange-500 border-orange-500 text-black' : 'text-gray-400 border-gray-800'}`} data-testid="siq-select-mode-drip">Drip · Weekly</button>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1.5 font-black">Note (audit)</div>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Scholarship comp — Coach B approval 2/26" className="w-full bg-black border-2 border-gray-800 px-3 py-2 text-sm focus:border-orange-500 outline-none" data-testid="siq-comp-note" />
        </div>
        <button onClick={comp} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-black py-3 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60" data-testid="siq-grant-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />} Grant {SPORT_IQ_SPORTS.find((s) => s.key === sport).label} · {mode.toUpperCase()}
        </button>
      </div>
    </Modal>
  );
};

const AuditModal = ({ onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/academy/admin/audit-log', { headers: authHeaders() })
      .then((r) => setEntries(r.data.entries || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  return (
    <Modal title="Change Log" onClose={onClose} testid="audit-modal" wide>
      {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div> : (
        entries.length === 0 ? <p className="text-sm text-gray-500 py-6 text-center">No changes recorded yet.</p> : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {entries.map((e, i) => (
              <div key={i} className="border border-gray-900 p-3 text-xs" data-testid={`audit-entry-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-black uppercase tracking-wider text-orange-400">{e.action}</span>
                  <span className="text-gray-600">{(e.at || '').replace('T', ' ').slice(0, 16)} UTC</span>
                </div>
                <div className="text-gray-400 mt-1">by <b className="text-gray-300">{e.actor_email}</b>{e.target_email ? <> → <b className="text-gray-300">{e.target_email}</b></> : null}</div>
                {e.changes && Object.keys(e.changes).length > 0 && (
                  <pre className="text-[10px] text-gray-500 mt-1 whitespace-pre-wrap break-all">{JSON.stringify(e.changes)}</pre>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </Modal>
  );
};

const Modal = ({ title, onClose, children, testid, wide }) => (
  <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
    <div className={`w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} bg-zinc-950 border-2 border-gray-800 p-5`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-black uppercase tracking-[0.2em] text-white truncate">{title}</h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="modal-close"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

export default AdminUsers;
